// DisclaimerPage.jsx  (print-safe + PDF-fit)
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';


const DisclaimerPage = ({ primary = '#0A68FF', company = 'StealthNet AI'}) => {

  const pDark = darken(primary, 0.35);
  const pMid = darken(primary, 0.15);
  const pLight = lighten(primary, 0.25);
  const ink = '#0f172a';

  return (
    <Box
      // Outer wrapper sized for US Letter printable area
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        my: 1,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <Box
        sx={{
          // Keep content within the PDF page width (approx 8.5in minus margins)
          width: '100%',
          maxWidth: '8.5in',          // <= key: fits within typical 0.5in margins
          bgcolor: '#fff',
          borderRadius: 1.5,
          border: `1px solid ${alpha(ink, 0.08)}`,
          overflow: 'hidden',
          display: 'flex',
          '@media print': {
            borderRadius: 0,
          },
        }}
      >
        {/* Slim cyber spine */}
       

        {/* Main content */}
        <Box sx={{ position: 'relative', flex: 1 }}>
          {/* Top accent ribbon (print-safe fallback) */}
          

          {/* Soft cyber grid */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)',
              backgroundSize: '24px 24px, 24px 24px',
              maskImage: 'radial-gradient(7in 4in at 45% 30%, black 45%, transparent 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Watermark (scaled to fit page) */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.06,
              fontSize: '64pt',           // print-safe cap
              fontWeight: 900,
              letterSpacing: 6,
              color: ink,
              transform: 'rotate(-16deg)',
              '@media print': { opacity: 0.06 },
            }}
          >
            CONFIDENTIAL
          </Box>

          {/* Padded content area (use inches for predictable print sizing) */}
          <Box sx={{ p: '0.6in' }}>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: '0.15in' }}>
              <Box
                sx={{
                  width: '0.45in', height: '0.45in', borderRadius: 2,
                  background: alpha(primary, 0.12),
                  border: `1px solid ${alpha(primary, 0.25)}`,
                  display: 'grid', placeItems: 'center',
                  color: pMid,
                }}
                title="Confidential"
              >
                <LockOutlinedIcon />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: ink }}>
                  Confidentiality Notice
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(ink, 0.6) }}>
                  Please read before sharing or distributing this document.
                </Typography>
              </Box>
              <Chip
                icon={<VisibilityOffOutlinedIcon />}
                label="CONFIDENTIAL"
                size="small"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  bgcolor: alpha('#B00020', 0.12),
                  color: '#B00020',
                  border: '1px solid',
                  borderColor: alpha('#B00020', 0.4),
                  borderRadius: '999px',
                }}
              />
            </Stack>

            <Divider
              sx={{
                mb: '0.2in',
                borderColor: alpha(ink, 0.08),
                '&::after': {
                  content: '""',
                  display: 'block',
                  height: 3,
                  width: '2.2in',
                  mt: 1,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${alpha(pDark, 0.9)}, ${alpha(pMid, 0.9)} 60%, ${alpha(pLight, 0.9)})`,
                },
                '@media print': {
                  '&::after': { background: pMid },
                },
              }}
            />

            <Typography variant="body1" sx={{ lineHeight: 1.65, color: alpha(ink, 0.92), mb: '0.12in' }}>
              This report contains proprietary and confidential information intended solely for the authorized
              recipient. Any review, use, disclosure, distribution, or copying by persons other than the intended
              recipient is strictly prohibited.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.65, color: alpha(ink, 0.92), mb: '0.12in' }}>
              Findings reflect the environment observed during the engagement and may change over time.
              Remediation actions should be evaluated and implemented by qualified personnel. Neither the authors
              nor their affiliates are responsible for any misuse of the information contained herein.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.65, color: alpha(ink, 0.92), mb: '0.2in' }}>
              By accessing this document, you agree to protect its confidentiality and to limit access to
              individuals with a legitimate business need.
            </Typography>

            {/* Meta band */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: '0.12in',
                mb: '0.22in',
              }}
            >
              {[
                {
                  label: 'Classification',
                  value: (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <ShieldOutlinedIcon sx={{ fontSize: 18, color: pMid }} />
                      <Typography variant="body2" sx={{ fontWeight: 800, color: ink }}>
                        Confidential
                      </Typography>
                    </Stack>
                  ),
                },
                {
                  label: 'Generated',
                  value: (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: ink }}>
                      {new Date().toLocaleDateString()}
                    </Typography>
                  ),
                },
                {
                  label: 'Distribution',
                  value: (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: ink }}>
                      Need-to-know only
                    </Typography>
                  ),
                },
              ].map((b, i) => (
                <Box
                  key={i}
                  sx={{
                    p: '0.12in',
                    border: `1px solid ${alpha(ink, 0.08)}`,
                    borderRadius: 1.25,
                    background: alpha(primary, 0.04),
                  }}
                >
                  <Typography variant="caption" sx={{ color: alpha(ink, 0.6) }}>
                    {b.label}
                  </Typography>
                  {b.value}
                </Box>
              ))}
            </Box>

            {/* Cyber chips (compact to avoid overflow) */}
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: '0.25in' }}>
              <Chip
                icon={<ReportGmailerrorredOutlinedIcon />}
                label="Unauthorized disclosure prohibited"
                size="small"
                sx={{
                  bgcolor: alpha(primary, 0.08),
                  color: pMid,
                  border: `1px solid ${alpha(primary, 0.18)}`,
                  fontWeight: 700,
                }}
              />
              <Chip
                icon={<VisibilityOffOutlinedIcon />}
                label="Limit access: business need-only"
                size="small"
                sx={{
                  bgcolor: alpha(pLight, 0.12),
                  color: pMid,
                  border: `1px solid ${alpha(primary, 0.18)}`,
                  fontWeight: 700,
                }}
              />
              <Chip
                icon={<LockOutlinedIcon />}
                label={`${company || 'StealthNet AI'} Property`}
                size="small"
                sx={{
                  bgcolor: alpha(darken(primary, 0.25), 0.15),
                  color: pMid,
                  border: `1px solid ${alpha(primary, 0.2)}`,
                  fontWeight: 800,
                }}
              />
            </Stack>

            {/* Microtext notice */}
            <Box
              sx={{
                borderRadius: 1.25,
                border: `1px dashed ${alpha(primary, 0.35)}`,
                background: alpha(primary, 0.04),
                p: '0.12in',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: alpha(ink, 0.75),
                  letterSpacing: 0.3,
                  lineHeight: 1.6,
                }}
              >
                <strong>Notice:</strong> This document contains sensitive security information produced during a
                professional assessment. Redistributing without prior written authorization from{' '}
                {company || 'StealthNet AI'} is strictly prohibited. Handle, transmit, and store according to your
                organization’s information handling policies.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DisclaimerPage;
